import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Handshake, Building2, Sofa, Wrench, Home as HomeIcon,
  CalendarClock, DollarSign, Headphones, UserCheck, Bell,
  ArrowRight, CheckCircle, Send, Loader2, Phone, ChevronRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const businessTypes = [
  'Furniture Store',
  'Appliance Retailer',
  'Contractor / Builder',
  'Property Manager / Leasing Office',
  'Warehouse / Distribution',
  'E-commerce / Retail',
  'Other',
];

const Partners = () => {
  const [form, setForm] = useState({
    business_name: '', contact_name: '', email: '', phone: '',
    business_type: '', delivery_volume: '', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState('');

  const fadeUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-50px' },
    transition: { duration: 0.6 },
  };

  const stagger = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
  };

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.business_name || !form.contact_name || !form.email || !form.phone || !form.business_type) {
      setError('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/partners`, form);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const benefits = [
    { icon: CalendarClock, title: 'Priority Scheduling', desc: 'Your deliveries are prioritized to keep your operations running smoothly.', color: 'brand-red' },
    { icon: DollarSign, title: 'Contract-Based Pricing', desc: 'Predictable, cost-efficient rates tailored to your volume.', color: 'brand-blue' },
    { icon: Headphones, title: 'Dedicated Support', desc: 'Direct communication with our team — no delays, no confusion.', color: 'brand-red' },
    { icon: UserCheck, title: 'Professional Representation', desc: 'Uniformed drivers and clean equipment representing your brand with care.', color: 'brand-blue' },
    { icon: Bell, title: 'Real-Time Delivery Updates', desc: 'Stay informed with status updates and delivery visibility.', color: 'brand-red' },
  ];

  const audiences = [
    { icon: Sofa, label: 'Furniture Stores' },
    { icon: Building2, label: 'Appliance Retailers' },
    { icon: Wrench, label: 'Contractors & Builders' },
    { icon: HomeIcon, label: 'Property Managers & Leasing Offices' },
  ];

  const steps = [
    { num: '01', title: 'Submit a partnership request', desc: 'Fill out the form below with your business details.' },
    { num: '02', title: 'We review your needs', desc: 'Our team assesses your delivery volume and requirements.' },
    { num: '03', title: 'Establish pricing & expectations', desc: 'We build a custom plan tailored to your operations.' },
    { num: '04', title: 'Start scheduling deliveries', desc: 'You begin with priority support and dedicated service.' },
  ];

  return (
    <div className="bg-texture min-h-screen" data-testid="partners-page">
      {/* HERO */}
      <section className="relative py-24 md:py-32 overflow-hidden" data-testid="partners-hero">
        <div className="absolute inset-0 bg-brand-surface" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <motion.div {...fadeUp}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-red/10 border border-brand-red/20 mb-8">
                <Handshake className="w-4 h-4 text-brand-red" />
                <span className="text-brand-red text-sm font-medium tracking-wide">PARTNER PROGRAM</span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-6">
                A Dedicated Delivery Partner{' '}
                <span className="text-brand-red">You Can Count On</span>
              </h1>
              <p className="text-slate-400 text-base md:text-lg leading-relaxed mb-10 max-w-2xl">
                TopFleet Logistics partners with local businesses to provide reliable, professional delivery services with priority scheduling and contract-based pricing.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="#partner-form"
                  className="btn-cta text-center px-8 py-4 text-base"
                  data-testid="hero-cta-primary"
                >
                  Request a Business Account
                </a>
                <a
                  href="tel:224-374-7400"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl border border-white/15 text-white hover:bg-white/5 transition-all font-medium"
                  data-testid="hero-cta-secondary"
                >
                  <Phone className="w-4 h-4" />
                  Speak With Our Team
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHO THIS IS FOR */}
      <section className="py-20 md:py-28 border-b border-white/5" data-testid="partners-audience">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl text-white mb-4">
              Built for Businesses That Need{' '}
              <span className="text-brand-red">Reliable Delivery</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto text-base md:text-lg">
              If your business depends on consistent, on-time deliveries, TopFleet is built to support your operations.
            </p>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {audiences.map((a, i) => (
              <motion.div
                key={a.label}
                {...stagger}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group bg-brand-surface rounded-2xl border border-white/10 p-8 text-center hover:border-brand-red/30 transition-all duration-300"
                data-testid={`audience-card-${i}`}
              >
                <div className="w-14 h-14 rounded-xl bg-brand-red/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-brand-red/20 transition-colors">
                  <a.icon className="w-7 h-7 text-brand-red" />
                </div>
                <p className="text-white font-medium text-sm">{a.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNER BENEFITS */}
      <section className="py-20 md:py-28 border-b border-white/5" data-testid="partners-benefits">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl text-white mb-4">
              Why Businesses <span className="text-brand-red">Partner</span> With TopFleet
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => (
              <motion.div
                key={b.title}
                {...stagger}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`bg-brand-surface rounded-2xl border border-white/10 p-8 hover:border-${b.color}/30 transition-all duration-300 ${i === 4 ? 'md:col-span-2 lg:col-span-1' : ''}`}
                data-testid={`benefit-card-${i}`}
              >
                <div className={`w-12 h-12 rounded-xl bg-${b.color}/10 flex items-center justify-center mb-5`}>
                  <b.icon className={`w-6 h-6 text-${b.color}`} />
                </div>
                <h3 className="font-heading text-white text-lg mb-2">{b.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 md:py-28 border-b border-white/5" data-testid="partners-how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl text-white mb-4">
              Simple. Reliable. <span className="text-brand-red">Built Around Your Needs.</span>
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <motion.div
                key={s.num}
                {...stagger}
                transition={{ duration: 0.5, delay: i * 0.12 }}
                className="relative bg-brand-surface rounded-2xl border border-white/10 p-8"
                data-testid={`step-card-${i}`}
              >
                <span className="font-heading text-5xl text-brand-red/15 absolute top-4 right-6">{s.num}</span>
                <div className="relative">
                  <h3 className="font-heading text-white text-base mb-2 pr-8">{s.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{s.desc}</p>
                </div>
                {i < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <ChevronRight className="w-5 h-5 text-white/20" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Guarantee for B2B */}
      <section className="py-16 border-b border-white/5" data-testid="partners-guarantee">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="flex flex-col md:flex-row items-center gap-8 py-8 px-10 rounded-2xl bg-brand-surface border border-white/10">
            <div className="w-16 h-16 rounded-2xl bg-brand-red/10 flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-8 h-8 text-brand-red" />
            </div>
            <div className="text-center md:text-left">
              <h3 className="font-heading text-xl text-white mb-2">Guaranteed Service Reliability for Your Business</h3>
              <p className="text-slate-400 text-sm leading-relaxed max-w-2xl">
                Every TopFleet partnership is backed by our Service Guarantee — on-time delivery commitment, full damage protection, and professional accountability. Your operations depend on consistency, and we deliver it.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA + FORM */}
      <section id="partner-form" className="py-20 md:py-28 scroll-mt-24" data-testid="partners-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 items-start">
            {/* Left: Info */}
            <div className="lg:col-span-2">
              <motion.div {...fadeUp}>
                <h2 className="font-heading text-3xl sm:text-4xl text-white mb-4">
                  Get Started With{' '}
                  <span style={{ color: '#DC2626' }}>TOP</span>
                  <span className="text-white">FLEET</span>
                </h2>
                <p className="text-slate-400 mb-8 leading-relaxed">
                  Tell us about your business and delivery needs. Our team will review your request and reach out within 1-2 business days to discuss a custom partnership plan.
                </p>
                <div className="space-y-4">
                  {['No long-term commitment required', 'Custom pricing based on volume', 'Dedicated account manager', 'Priority scheduling from day one'].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                      <span className="text-slate-300 text-sm">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-10 p-6 rounded-2xl bg-brand-surface border border-white/10">
                  <p className="text-white font-heading text-sm mb-2">PREFER TO TALK?</p>
                  <a href="tel:224-374-7400" className="text-brand-red font-heading text-2xl hover:underline">224-374-7400</a>
                  <p className="text-slate-500 text-xs mt-2">Mon-Sat, 7AM - 7PM EST</p>
                </div>
              </motion.div>
            </div>

            {/* Right: Form */}
            <div className="lg:col-span-3">
              <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.1 }}>
                {submitted ? (
                  <div className="bg-brand-surface rounded-2xl border border-green-500/20 p-12 text-center" data-testid="partner-form-success">
                    <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-8 h-8 text-green-400" />
                    </div>
                    <h3 className="font-heading text-2xl text-white mb-3">Partnership Request Submitted</h3>
                    <p className="text-slate-400 mb-6 max-w-md mx-auto">
                      Thank you for your interest! Our business development team will review your request and contact you within 1-2 business days.
                    </p>
                    <Link to="/" className="text-brand-red hover:underline font-medium inline-flex items-center gap-1">
                      Back to Home <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    className="bg-brand-surface rounded-2xl border border-white/10 p-8 md:p-10 space-y-6"
                    data-testid="partner-form"
                  >
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className="text-xs text-slate-400 uppercase tracking-wider mb-1.5 block">Business Name *</label>
                        <input
                          type="text" name="business_name" value={form.business_name} onChange={handleChange} required
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-600 focus:border-brand-red outline-none transition-colors"
                          placeholder="Your company name"
                          data-testid="partner-business-name"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 uppercase tracking-wider mb-1.5 block">Contact Name *</label>
                        <input
                          type="text" name="contact_name" value={form.contact_name} onChange={handleChange} required
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-600 focus:border-brand-red outline-none transition-colors"
                          placeholder="Your full name"
                          data-testid="partner-contact-name"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className="text-xs text-slate-400 uppercase tracking-wider mb-1.5 block">Email *</label>
                        <input
                          type="email" name="email" value={form.email} onChange={handleChange} required
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-600 focus:border-brand-red outline-none transition-colors"
                          placeholder="you@company.com"
                          data-testid="partner-email"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 uppercase tracking-wider mb-1.5 block">Phone Number *</label>
                        <input
                          type="tel" name="phone" value={form.phone} onChange={handleChange} required
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-600 focus:border-brand-red outline-none transition-colors"
                          placeholder="(555) 123-4567"
                          data-testid="partner-phone"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className="text-xs text-slate-400 uppercase tracking-wider mb-1.5 block">Business Type *</label>
                        <select
                          name="business_type" value={form.business_type} onChange={handleChange} required
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:border-brand-red outline-none transition-colors appearance-none"
                          style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2394a3b8' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center' }}
                          data-testid="partner-business-type"
                        >
                          <option value="" disabled className="bg-brand-navy">Select your industry</option>
                          {businessTypes.map(t => (
                            <option key={t} value={t} className="bg-brand-navy">{t}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 uppercase tracking-wider mb-1.5 block">Est. Monthly Deliveries</label>
                        <input
                          type="text" name="delivery_volume" value={form.delivery_volume} onChange={handleChange}
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-600 focus:border-brand-red outline-none transition-colors"
                          placeholder="e.g. 20-50 per month"
                          data-testid="partner-delivery-volume"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-xs text-slate-400 uppercase tracking-wider mb-1.5 block">Message / Notes</label>
                      <textarea
                        name="message" value={form.message} onChange={handleChange} rows={4}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-600 focus:border-brand-red outline-none resize-none transition-colors"
                        placeholder="Tell us about your delivery needs, preferred schedule, or any questions..."
                        data-testid="partner-message"
                      />
                    </div>

                    {error && (
                      <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 p-3 rounded-xl" data-testid="partner-form-error">
                        {error}
                      </div>
                    )}

                    {/* Consent Checkbox */}
                    <label className="flex items-start gap-3 cursor-pointer" data-testid="partner-consent-label">
                      <input
                        type="checkbox"
                        checked={agreed}
                        onChange={(e) => setAgreed(e.target.checked)}
                        className="mt-1 w-4 h-4 rounded border-white/20 bg-white/5 text-brand-red focus:ring-brand-red/50 cursor-pointer"
                        data-testid="partner-consent-checkbox"
                        required
                      />
                      <span className="text-slate-400 text-sm leading-relaxed">
                        I agree to the{' '}
                        <Link to="/legal" className="text-brand-red hover:underline" target="_blank">Legal & Policies</Link>
                      </span>
                    </label>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-4 bg-brand-red hover:bg-red-700 text-white font-heading text-base rounded-xl transition-all flex items-center justify-center gap-3 disabled:opacity-50"
                      data-testid="partner-submit-button"
                    >
                      {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                      {submitting ? 'SUBMITTING...' : 'REQUEST PARTNERSHIP'}
                    </button>

                    <p className="text-slate-600 text-xs text-center">
                      By submitting, you agree to be contacted by our business development team.
                    </p>
                  </form>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Partners;
